import * as types from '../actiontypes';

import {
	CopyDocumentNameBehaviorType,
	DocumentArrayFilters,
	DocumentMergeFilters,
	DocumentSource,
	DrillDownMiscConstants,
	documentTypes,
	promiseAreas,
	serviceURL,
	taskApiRoutes,
	daasOptions
} from '../../util/uiconstants';

import Env from '../../util/env';
import Utility from '../../util/utilityfunctions';
import {stringify} from 'query-string';

export const getAllDocuments = (url, resolve = false) => {
	return {
		type: types.GET_ALL_DOCUMENTS,
		resolve: resolve,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		}
	};
};

// TODO: This is an Account action, move to accountactions
export const getAccountByDocumentId = (url) => {
	return {
		type: types.GET_ACCOUNT_BY_DOCUMENTID,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		}
	};
};

/**
 * Build the URL with the engagementId and paramters.
 * @param {number} engagementId - engagement Id.
 * @param {object} filters - parameters to make the get call.
 * @returns {string} complete URL to make the call.
 * */
const concatenateURL = (engagementId, filters) => {
	let url = new URL(`${Env.getBaseURL(serviceURL, engagementId)}documents`);
	let skipFilter = false;

	/*remove duplicates from the array*/
	for (let key in filters) {
		// If the value is an array
		if (Array.isArray(filters[key])) {
			// Convert the array to a Set to remove duplicates, then convert it back to an array
			filters[key] = [...new Set(filters[key])];
		}
	}
	if (filters) {
		Object.entries(filters).forEach((filter) => {
			if (filter[1] !== undefined && filter[1] !== null) {
				if (Array.isArray(filter[1])) {
					const separator = ',';

					let filterString = '';
					for (let i = 0; i < filter[1].length; i++) {
						if (filterString !== '') {
							filterString += separator;
						}

						filterString += filter[1][i];
					}
					if (DocumentArrayFilters.includes(filter[0].toLowerCase())) {
						filter[1].forEach((filterVal) => {
							url.searchParams.append(filter[0], filterVal);
						});
					} else if (DocumentMergeFilters.includes(filter[0].toLowerCase())) {
						if (!skipFilter) {
							let timephases = [];
							let secondaryFilterVal = '';
							if (filter[0].toLowerCase() === DrillDownMiscConstants.filtertimephase) {
								secondaryFilterVal = DrillDownMiscConstants.timephase;
							} else {
								secondaryFilterVal = DrillDownMiscConstants.filtertimephase;
							}
							timephases = _.isArray(filter[1]) ? [...filter[1]] : [filter[1]];
							if (_.keys(filters).includes(secondaryFilterVal)) {
								let secondaryFilters = filters[secondaryFilterVal];
								timephases = _.isNull(secondaryFilters)
									? timephases
									: _.isArray(secondaryFilters)
									? [...timephases, ...secondaryFilters]
									: [...timephases, secondaryFilters];
								timephases = _.uniq(timephases);
							}

							url.searchParams.append(DrillDownMiscConstants.filtertimephase, _.join(timephases, separator));
							skipFilter = true;
						}
					} else {
						url.searchParams.append(filter[0], filterString);
					}
				} else {
					url.searchParams.append(filter[0], filter[1]);
				}
			}
		});
	}

	return url.toString();
};

/**
 * Makes a call to api/engagements/{engagementId}/documents using the state documents.
 * @param {number} engagementId - engagement Id.
 * @param {object} filters - parameters off the get call.
 * @returns {{type: string, meta: object}} Object to make the API call.
 * */
export const getAllDocs = (engagementId, filters) => {
	let url = concatenateURL(engagementId, filters);

	return {
		type: types.GET_ALL_DOCUMENTS_SEARCH,
		resolve: true,
		meta: {
			api: {
				url: url,
				method: 'GET'
			}
		}
	};
};

/**
 * Clean the values of the stete documents.
 * @returns {{type: String}} - Object with the action type.
 * */
export const cleanGetAllDocs = () => {
	return {
		type: types.GET_ALL_DOCUMENTS_CLEAN
	};
};

export const cleanDocument = () => {
	return {
		type: types.CLEAN_DOCUMENT
	};
};

// Makes call to getAll and adds tracking for Key Documents entity details tile.
export function getAllKeyDocuments(engagementId, filters, parentName, filterKey) {
	let getAllCall = getAll(engagementId, filters, parentName, filterKey);
	getAllCall.meta.api = {
		...getAllCall.meta.api,
		trackWithArea: promiseAreas.GET_KEY_DOCUMENTS
	};
	return getAllCall;
}

// Makes a call to api/engagements/{engagementId}/documents with specified filters
export function getAll(engagementId, filters, parentName, filterKey, payload) {
	let renderFilters;
	Object.values(filters).forEach((level) => {
		if (!renderFilters) {
			renderFilters = {};
		}

		renderFilters = {
			...renderFilters,
			...level
		};
	});

	if (!renderFilters.searchstring && renderFilters.searchtype) {
		delete renderFilters.searchtype;
	}

	let url = concatenateURL(engagementId, renderFilters);

	return {
		type: types.GET_DOCUMENT_LIST,
		engagementId: engagementId,
		parentName: parentName,
		filters: filters,
		filterKey,
		filteredEntityIdGuid: renderFilters.filteredEntityIdGuid,
		resolve: true,
		payload,
		meta: {
			isDocListUrl: true,
			api: {
				url: url,
				method: 'GET',
				trackWithArea: promiseAreas.GET_ALL_DOCUMENTS
			}
		}
	};
}

// Makes a call to api/engagements/{engagementId}/documents/{documentId} with specified include clause
export function getDocumentById(
	engagementId,
	documentId,
	parentName,
	expand,
	filteredEntityIdGuid = null,
	fromUpdate = false,
	queryStringParameters,
	actionProps,
	resolve = false
) {
	let url = getDocumentUrl(engagementId, documentId, expand, undefined, queryStringParameters);
	return {
		type: types.GET_DOCUMENT_BY_ID,
		resolve: resolve,
		engagementId: engagementId,
		parentName: parentName,
		documentId: documentId,
		filteredEntityIdGuid,
		fromUpdate,
		...actionProps,
		meta: {
			api: {
				url: url,
				method: 'GET'
			}
		}
	};
}

// Makes a call to api/engagements/{engagementId}/documents/{id} with PATCH http verb
export function updateDocument(
	engagementId,
	documentId,
	data,
	resolvedversionNumber,
	currentVersionNumber,
	isHistoryResolve,
	operationType = 'Update'
) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}`;

	if (resolvedversionNumber && resolvedversionNumber > 0) {
		url =
			url +
			'?resolvedVersionNumber=' +
			resolvedversionNumber +
			'&currentVersionNumber=' +
			currentVersionNumber +
			'&isHistoryResolve=' +
			isHistoryResolve;
	}

	if (data?.value !== undefined) {
		const {value: documentName, ...rest} = data;
		const updatedData = {documentName, operationType: operationType, ...rest};

		return {
			type: types.UPDATE_DOCUMENT,
			meta: {
				api: {
					url: url,
					method: 'PATCH',
					data: updatedData
				}
			}
		};
	} else {
		return {
			type: types.UPDATE_DOCUMENT,
			meta: {
				api: {
					url: url,
					method: 'PATCH',
					data: data
				}
			}
		};
	}
}

export function patchDocumentVersion(engagementId, documentId, versionNumber, patch) {
	const url = getDocumentUrl(engagementId, documentId) + '/Versions/' + versionNumber;

	return {
		type: types.PATCH_DOCUMENT_VERSION,
		documentId,
		meta: {
			api: {
				url,
				method: 'PATCH',
				data: patch
			}
		}
	};
}

export function restoreDocumentVersion(engagementId, documentId, versionNumber) {
	const patch = [
		{
			path: '/isCurrentVersion',
			op: 'replace',
			value: 'true'
		}
	];

	return patchDocumentVersion(engagementId, documentId, versionNumber, patch);
}

export function addApproval(engagementId, documentId, data, parentName) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}/approvals`;

	return {
		type: types.ADD_APPROVAL_FOR_DOCUMENT,
		parentName: parentName,
		documentId: documentId,
		resolve: true,
		meta: {
			api: {
				url: url,
				method: 'POST',
				data: data
			}
		}
	};
}

export function createCanvasFormSnapshot(engagementId, documentId, approvalId) {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}Forms/${documentId}/Snapshots`;

	const data = {ApprovalId: approvalId || 0};

	return {
		type: types.CREATE_SNAPSHOT,
		meta: {
			api: {
				url: url,
				method: 'POST',
				data: data
			}
		}
	};
}

export function removeApproval(engagementId, documentId, approvalId, parentName) {
	let requestURL = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}/approvals/${approvalId}`;

	let data = {
		approvalId: approvalId,
		documentId: documentId,
		engagementId: engagementId
	};

	return {
		type: types.REMOVE_APPROVAL_FOR_DOCUMENT,
		parentName: parentName,
		documentId: documentId,
		approvalId: approvalId,
		meta: {
			api: {
				url: requestURL,
				method: 'DELETE',
				data: data
			}
		}
	};
}

export function deleteDocumentById(engagementId, documentId, parentName = undefined, action = 'delete') {
	const url = getDocumentUrl(engagementId, documentId, undefined, action);

	return {
		type: types.DELETE_DOCUMENT_BY_ID,
		parentName,
		documentId,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			}
		}
	};
}

export function deleteDocumentVersionById(engagementId, parentName, documentId, documentVersionId, hardDelete = false) {
	return {
		type: types.DELETE_DOCUMENT_VERSION_BY_ID,
		parentName,
		documentId,
		documentVersionId,
		meta: {
			api: {
				url: Env.getDocServiceUrl(
					`documents.json/${documentId}/versions/${documentVersionId}?hardDelete=${hardDelete}`
				),
				method: 'DELETE',
				headers: {
					engagementid: engagementId
				}
			}
		}
	};
}

//#region Common
function getDocumentUrl(engagementId, documentId, expand, action, queryStringParameters) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}`;

	if (action) {
		if (action === 'HardDelete') {
			url += `/HardDelete`;
		}
	}

	const _queryStringParameters = queryStringParameters ? {...queryStringParameters} : {};
	if (expand) {
		_queryStringParameters.Expand = expand;
	}

	let urlParameters = '';

	if (_queryStringParameters) {
		Object.entries(_queryStringParameters).forEach((entry, idx) => {
			urlParameters += idx === 0 ? '' : '&';
			urlParameters += `${entry[0]}=${entry[1]}`;
		});
	}

	if (urlParameters !== '') {
		url += '?' + urlParameters;
	}

	return url;
}
//#endregion

//#region DocumentList

//#region Documents API Management
// Makes a call to api/engagements/{engagementId}/documents/{id} with 'VERSIONS' include clause
export function getDocumentHistory(engagementId, documentId, parentName, isHistory) {
	const vertionType = isHistory ? 'versions' : 'conflicts',
		url = getDocumentUrl(engagementId, documentId, vertionType);

	return {
		type: types.GET_DOCUMENT_HISTORY,
		engagementId: engagementId,
		parentName: parentName,
		documentId: documentId,
		meta: {
			api: {
				url: url,
				method: 'GET'
			}
		}
	};
}

export function setIsModalOpen(isModalOpen) {
	return {
		type: types.SET_IS_MODAL_OPEN,
		isModalOpen: isModalOpen === undefined ? false : isModalOpen
	};
}

// Unlinks a document from an engagement
// Makes a call to api/engagements/{engagementId}/documents/{documentId}/LinkedEvidence
export function unlinkSharedDocument(engagementId, documentId, parentName, relatedEntityId) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents`;

	return {
		type: types.UNLINK_SHARED_DOCUMENT,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: [
					{
						value: documentId,
						path: '/UnlinkEvidence',
						op: 'remove'
					}
				]
			}
		},
		parentName,
		relatedEntityId,
		payload: {
			documentId
		}
	};
}

//#endregion

//#region Redux state management
export function refresh(parentName, resetPage = false) {
	return {
		type: types.REFRESH_DOCUMENT_LIST,
		parentName: parentName,
		resetPage: resetPage
	};
}

export function addDocumentToList(parentName, document) {
	return {
		type: types.ADD_DOCUMENT_TO_LIST,
		parentName,
		document
	};
}

export function setDocumentListFilters(filterKey, parentName, filters) {
	return {
		type: types.SET_DOCUMENT_LIST_FILTERS,
		filterKey,
		parentName,
		filters
	};
}

export function setDocumentListMultiEntityView(parentName, view) {
	return {
		type: types.SET_DOCUMENT_LIST_ENTITY_VIEW,
		parentName,
		view
	};
}

export function mergeDocumentListFilters(filterKey, parentName, filters) {
	return {
		type: types.MERGE_DOCUMENT_LIST_FILTERS,
		filterKey,
		parentName,
		filters
	};
}

export function setDocumentListFilter(parentName, filterKey, filterItemName, value) {
	return {
		type: types.SET_DOCUMENT_LIST_FILTER,
		parentName,
		filterKey,
		filterItemName,
		value
	};
}

export function clearDocumentListFilters(parentName, filterKey, filterState = undefined, refresh = undefined) {
	return {
		type: types.CLEAR_DOCUMENT_LIST_FILTERS,
		parentName,
		filterKey,
		filterState,
		refresh
	};
}

export function clearAll(parentName) {
	return {
		type: types.CLEAR_DOCUMENT_LIST,
		parentName
	};
}

//Is like clearAll but here we also remove the upload property, that will remove the uploaded documents.
export function resetDocList(parentName) {
	return {
		type: types.RESET_DOCUMENT_LIST,
		parentName
	};
}

export function clearCustomFilters(parentName, filterKey) {
	return {
		type: types.CLEAR_CUSTOM_FILTERS,
		parentName,
		filterKey
	};
}

export function clearDocumentById(parentName, documentId) {
	return {
		type: types.CLEAR_DOCUMENT_BY_ID,
		parentName,
		documentId
	};
}

export function updateDocumentById(parentName, documentId, payload) {
	return {
		type: types.UPDATE_DOCUMENT_LIST_BY_ID,
		parentName: parentName,
		documentId: documentId,
		payload: payload
	};
}

export function updateDocumentsByParentName(parentName, payload = []) {
	return {
		type: types.UPDATE_DOCUMENT_LIST_BY_PARENTNAME,
		parentName,
		payload
	};
}

export function clearDocumentHistoryById(parentName, documentId) {
	return {
		type: types.CLEAR_DOCUMENT_LIST_HISTORY,
		parentName: parentName,
		documentId: documentId
	};
}

export function clearTaskIdData() {
	return {
		type: types.CLEAR_TASKID_DATA
	};
}

// Action to get related reference documents for engagement or user.
export function getOnlyRelatedReferenceDocs(referenceDocuments, tabName, optionPerPage, searchText, currentPage) {
	return {
		type: types.GET_RELATED_REFERENCE_DOCS,
		referenceDocuments,
		tabName,
		currentPage,
		optionPerPage,
		searchText
	};
}
//#endregion

//#endregion

// Unlinks all documents from an engagement
// Makes a call to api/engagements/{engagementId}/documents
export function unlinkAllSharedDocuments(engagementId) {
	let url = `${Env.getBaseURL(serviceURL, engagementId)}documents`;

	return {
		type: types.UNLINK_ALL_SHARED_DOCUMENTS,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: [
					{
						path: '/UnlinkAllEvidences',
						op: 'remove'
					}
				]
			}
		}
	};
}

export function getDocumentRelatedTasks(engagementId, documentId, queryString, parentName, resource) {
	let requestUrl = Env.getBaseURL(serviceURL, engagementId) + taskApiRoutes.task + '?';

	return {
		type: types.GET_DOCUMENT_RELATED_TASKS,
		resolve: true,
		documentId: documentId,
		parentName: parentName,
		meta: {
			api: {
				url: `${requestUrl}${stringify(queryString)}`,
				method: 'GET',
				data: resource
			}
		}
	};
}

export function createDocument(
	engagementId,
	userId,
	documentId,
	documentTypeId,
	customDocumentName,
	targetDocumentSource
) {
	return {
		type: types.CREATE_DOCUMENT,
		resolve: true,
		meta: {
			api: {
				url: Env.getDocServiceUrl('documents.json'),
				method: 'POST',
				headers: {
					'X-COPY-FROM': documentId,
					engagementId,
					ignoreFileTypeValidation: true,
					transactionId: userId
				},
				data: {
					targetEngagementId: engagementId,
					targetDocumentType: documentTypeId,
					targetDocumentSource,
					customDocumentName,
					copyNameBehavior: {
						behavior: CopyDocumentNameBehaviorType.CustomDocumentName
					}
				}
			}
		}
	};
}

export function addDocumentToEvidence(engagementId, documentId, taskIds, document) {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}taskdocuments`;

	return {
		type: types.ADD_DOCUMENT_TO_EVIDENCE,
		payload: document,
		meta: {
			api: {
				url,
				method: 'POST',
				data: {
					documentId,
					taskIds
				}
			}
		}
	};
}

export function relateCanvasForms(engagementId, documentId, sourceDocumentId, formIds) {
	const url = `${Env.getBaseURL(serviceURL, engagementId)}documents/${documentId}/formrelateddocuments`;

	return {
		type: types.RELATE_DOCUMENT_TO_CANVAS_FORMS,
		meta: {
			api: {
				url,
				method: 'POST',
				data: {
					sourceDocumentId,
					formIds
				}
			}
		}
	};
}

export function createAnalyticDocument(engagementId, engagementHelixId, bookmarkData, createUserId) {
	const url = `${Env.getDocServiceUrl('documentimports.json')}?engagementId=${engagementId}`;

	const newAnalyticDocument = {
		documentSourceId: DocumentSource.ImportFromHelix,
		documentTypeId: documentTypes.CLIENT_SHARED_HELIX_ANALYTIC,
		originalFileName: Utility.getSanitizedAnalyticFilename(bookmarkData.bookMarkName),
		isDeleted: false,
		externalStreamId: bookmarkData.analyzerId,
		externalStreamAddress: bookmarkData.bookmarkId.toString(),
		smartEvidenceSourceId: engagementHelixId,
		isContentAvailable: true,
		fileExt: '',
		documentName: Utility.getSanitizedAnalyticFilename(bookmarkData.bookMarkName),
		documentStatusId: null,
		createUserId: createUserId,
		engagementId: engagementId,
		createDate: new Date().toISOString(),
		helixJson: JSON.stringify({
			bookMarkViewUrl: bookmarkData.bookMarkViewUrl,
			createdBy: bookmarkData.createdBy,
			bookMarkEditUrl: bookmarkData.bookMarkEditUrl,
			clientInquiry: bookmarkData.clientInquiry,
			bookMarkName: bookmarkData.bookMarkName
		})
	};

	return {
		type: types.CREATE_ANALYTIC_DOCUMENT,
		meta: {
			api: {
				url,
				method: 'POST',
				data: newAnalyticDocument,
				headers: {
					engagementid: engagementId.toString()
				}
			},
			lastModifiedBy: bookmarkData.bookmarkCreatedByFullName
		},
		resolve: true
	};
}

export function daasCheckInCheckOutDocument(engagementId, documentId, parentName, actionType) {
	let url = Env.getDocServiceUrl(`documents.json/${documentId}/${actionType}`);

	return {
		type: types.CHECK_IN_CHECK_OUT_DOCUMENT,
		documentId: documentId,
		parentName: parentName,
		meta: {
			api: {
				url: url,
				method: 'POST',
				headers: {
					engagementid: engagementId
				}
			}
		}
	};
}
